import { parse } from 'query-string';
import { useEffect } from 'react';

import { useQuery as defaultUseQuery } from '@apollo/react-hooks';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import { isBrowser, isCrawler } from '../../utils';
import abTestQuery from './abTestQuery';
import type { ABTest, UseQuery } from './types';

type Options = {
  /**
   * Optional meta data to send along with the birth event.
   */
  birthEventMetadata?: {};
  /**
   * If this is `false`, it will disable the logging of the birth event.
   *
   * Note: You will need to implement your own birth event if you opt-out
   * of this behaviour. If you are unsure about this, it is recommended
   * you leave this `true`.
   */
  shouldTrackBirthEvent?: boolean;
  useQuery?: UseQuery;
};

const isStubbingIdentity = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const queryParams = parse(window.location.search);

  return Boolean(queryParams && queryParams.identity);
};

/**
 * Returns the variant the user is assigned based on the response from the
 * AB test API.
 */
const useABTest = (experimentName: string, options: Options = {}) => {
  const {
    shouldTrackBirthEvent = true,
    birthEventMetadata,
    useQuery = defaultUseQuery,
  } = options;

  const userAgent =
    typeof window === 'undefined'
      ? null
      : window.navigator && window.navigator.userAgent;

  // AB Tests should not be run on the server-side as they potentially
  // could impact our SEO. For this reason, we skip firing the request on
  // the server.
  const shouldSkip = !isBrowser() || isCrawler(userAgent);

  const { loading, error, ...result } = useQuery(abTestQuery, {
    skip: shouldSkip,
    ssr: false,
  });

  const abTests: Array<ABTest> = (result.data && result.data.abTests) || [];
  const targetAbtest = abTests.find(test => test.name === experimentName);
  const isEligible = targetAbtest && targetAbtest.isEligible;

  // Fallback to `control` if the test is not found or the user is ineligible
  // for it.
  const resultVariant =
    targetAbtest && isEligible ? targetAbtest.variant : 'control';

  const { trackBirthEvent } = useTrackingContext();

  useEffect(() => {
    // We only log events for users that are eligible for the AB test. We also
    // skip logging the event if the user is stubbing their identity using
    // `?identity=x`. As well, we allow the consumer to explicitly opt-out
    // of tracking by passing the `shouldTrackBirthEvent: false` flag.
    const shouldLogEvents = Boolean(
      isEligible && !isStubbingIdentity() && shouldTrackBirthEvent
    );

    // Logs a birth event whenever the component that renders the AB test is
    // mounted.
    if (shouldLogEvents) {
      trackBirthEvent(experimentName, resultVariant, birthEventMetadata);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    experimentName,
    isEligible,
    birthEventMetadata,
    resultVariant,
    shouldTrackBirthEvent,
  ]);

  return {
    isLoading: loading,
    error,
    variant: resultVariant,
  };
};

export default useABTest;
